//src/app/features/taxEstimates/TaxEstimateForm/FormFields.tsx
import { BusinessTypesOptions } from 'app/features/profile/businessDetails/addBusinessDetails/formFields';
import { ITaxEstimate, ITaxEstimateForm } from 'app/services/taxEstimates';
import { Fragment } from 'react';
import renderInput from 'utils/renderInput';
import { Validators } from 'utils/Validators';
import { taxYearsOptions, yearSelectOptions } from 'utils/yearSelectOptions';
import * as Yup from 'yup';

export const validationSchema = Yup.object().shape({
  // General
  year: Yup.string().nullable().required('Required'),
  type: Yup.string().nullable().required('Required'),
  business_id: Yup.string().when('type', {
    is: 'business',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  // Business Type
  businessType: Yup.string().when('type', {
    is: 'business',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  firstName: Yup.string().nullable().required('Required'),
  lastName: Yup.string().nullable().required('Required'),
  ssn: Yup.string().nullable().required('Required'),
  phoneNumber: Yup.string().nullable().required('Required'),
  email: Yup.string().nullable().required('Required'),

  // Spouse
  isMarried: Yup.string().when('type', {
    is: 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  spouseFirstName: Yup.string().when(['isMarried', 'type'], {
    is: (isMarried: string, type: string) =>
      isMarried === 'yes' && type === 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  spouseLastName: Yup.string().when(['isMarried', 'type'], {
    is: (isMarried: string, type: string) =>
      isMarried === 'yes' && type === 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  spouseSSN: Yup.string().when(['isMarried', 'type'], {
    is: (isMarried: string, type: string) =>
      isMarried === 'yes' && type === 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),

  // Dependenets
  haveDependents: Yup.string().when('type', {
    is: 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  depenendentsCountUnder16: Yup.string().when(['haveDependents', 'type'], {
    is: (haveDependents: string, type: string) =>
      haveDependents === 'yes' && type === 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  otherdepenendentsCount: Yup.string().when(['haveDependents', 'type'], {
    is: (haveDependents: string, type: string) =>
      haveDependents === 'yes' && type === 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),

  // Address
  address: Yup.string().nullable().required('Required'),
  city: Yup.string().nullable().required('Required'),
  state: Yup.string().nullable().required('Required'),
  zipCode: Validators.zipCode.required('Required'),
  liveOutsideOfUs: Yup.string().nullable().required('Required'),
  otherCountry: Yup.string().when('liveOutsideOfUs', {
    is: 'yes',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  provinceCounty: Yup.string().when('liveOutsideOfUs', {
    is: 'yes',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),

  // Income
  expectedBusinessProfitOrLoss: Yup.string().nullable().required('Required'),

  // Taxes
  federalEstimatedTaxesAlreadyPaidSoFarThisYear: Yup.string()
    .nullable()
    .required('Required'),
  stateEstimatedTaxesAlreadyPaidSoFarThisYear: Yup.string()
    .nullable()
    .required('Required'),

  agreement: Yup.boolean().nullable().isTrue('Required'),
});

export const TaxEstimateFields = [
  {
    type: 'information',
    subLabel: () => (
      <div className="text-sm text-gray-700">
        <span className="text-green font-semibold mr-2">Instructions</span>
        Please include all income and deductions received so far for the current
        year. For instance, if your business generally makes 100k profit
        annually, but you only made 70k so far this year, input 70k as the
        business profit
      </div>
    ),
  },

  {
    dependenciesTitle: 'General Info',
    type: 'information',
    dependencies: [
      {
        label: 'First Name *',
        name: 'firstName',
        type: 'text',
      },
      {
        label: 'Last Name *',
        name: 'lastName',
        type: 'text',
      },
      {
        label: 'Social Security Number *',
        name: 'ssn',
        type: 'maskedInput',
        mask: '999-99-9999',
      },
      {
        label: 'Phone Number *',
        name: 'phoneNumber',
        type: 'text',
      },
      {
        label: 'Email *',
        name: 'email',
        type: 'text',
      },
      {
        type: 'select',
        name: 'year',
        label: 'Tax Year *',
        options: yearSelectOptions,
      },
      {
        name: 'type',
        type: 'radio',
        label: 'Tax Type *',
        options: [
          { label: 'Individual', value: 'individual' },
          { label: 'Business', value: 'business' },
        ],
        dependencies: [
          {
            predicate: (v) => v === 'business',
            type: 'business',
            label: 'Select Business',
            name: 'business_id',
          },
          {
            predicate: (v) => v === 'business',
            type: 'select',
            name: 'businessType',
            label: 'Business Type',
            options: BusinessTypesOptions,
          },

          {
            label: 'Are you married ? *',
            name: 'isMarried',
            predicate: (v) => v === 'individual',
            type: 'radio',
            dependencies: [
              {
                type: 'text',
                label: 'Spouse’s First Name *',
                name: 'spouseFirstName',
                predicate: (v) => v === 'yes',
              },
              {
                type: 'text',
                label: 'Spouse’s Last Name *',
                name: 'spouseLastName',
                predicate: (v) => v === 'yes',
              },
              {
                type: 'maskedInput',
                label: 'Spouse’s Social Security Number *',
                name: 'spouseSSN',
                mask: '999-99-9999',
                predicate: (v) => v === 'yes',
              },
            ],
          },
          {
            label: 'Do you have dependents ? *',
            name: 'haveDependents',
            predicate: (v) => v === 'individual',
            type: 'radio',

            dependencies: [
              {
                predicate: (v) => v === 'yes',
                label: 'Number of dependents 16 years old or under *',
                name: 'depenendentsCountUnder16',

                type: 'number',
              },
              {
                predicate: (v) => v === 'yes',
                label: 'Number of other dependents *',
                name: 'otherdepenendentsCount',

                type: 'number',
              },
            ],
          },
        ],
      },
    ],
  },

  {
    dependenciesTitle: 'Home Address',
    type: 'information',
    dependencies: [
      {
        type: 'text',
        label: 'Address *',
        name: 'address',
      },

      {
        type: 'text',
        label: 'Apartment #',
        name: 'apartment',
      },

      {
        type: 'text',
        label: 'City *',
        name: 'city',
      },

      {
        type: 'state',
        label: 'State *',

        name: 'state',
      },
      {
        type: 'text',
        label: 'Zip Code *',
        name: 'zipCode',
      },

      {
        type: 'radio',
        name: 'liveOutsideOfUs',
        label: 'Do you live out side the United States? *',

        dependencies: [
          {
            predicate: (v) => v === 'yes',
            name: 'otherCountry',
            type: 'country',
            label: 'Country *',
          },
          {
            predicate: (v) => v === 'yes',
            name: 'provinceCounty',
            type: 'text',
            label: 'Province/County *',
          },
        ],
      },
    ],
  },

  {
    dependenciesTitle: 'Tuition',
    type: 'information',
    dependencies: [
      {
        type: 'currency',
        name: 'collegeTuitionAmount',
        label: 'Amount of College Tuition Paid, if Applicable',
      },
    ],
  },
  {
    dependenciesTitle: 'Income',
    type: 'information',
    dependencies: [
      {
        type: 'currency',

        label: 'Expected Business Profit or Loss *',
        name: 'expectedBusinessProfitOrLoss',
      },
      {
        type: 'currency',

        label:
          'Expected Business Profit or Loss from Personally Owned Rental Property',
        name: 'expectedBusinessProfitOrLossFromPersonallyOwnedRentalProperty',
      },
      {
        type: 'currency',

        label: 'Expected IRA or Pension Distribution',
        name: 'expectedIraOrPensionDistribution',
      },
      {
        type: 'currency',

        label: 'Federal Withholdings on IRA',
        name: 'federalWithholdingsOnIra',
      },
      {
        type: 'currency',

        label: 'State Withholdings on IRA',
        name: 'stateWithholdingsOnIra',
      },
      {
        type: 'currency',

        label: 'Taxpayer - Total Social Security Received so far this Year',
        name: 'taxpayerTotalSocialSecurityReceivedSoFarThisYear',
      },
      {
        type: 'currency',

        label:
          'Taxpayer - Total Unemployment Compensation Received so far this Year',
        name: 'taxpayerTotalUnemploymentCompensationReceivedSoFarThisYear',
      },
      {
        type: 'currency',

        label: 'Other Income',
        name: 'otherIncome',
      },
    ],
  },
  {
    dependenciesTitle: 'Taxes',
    type: 'information',
    dependencies: [
      {
        name: 'federalEstimatedTaxesAlreadyPaidSoFarThisYear',
        type: 'currency',
        label: 'Federal Estimated Taxes Already Paid so far this year *',
      },
      {
        label: 'State Estimated Taxes Already Paid so far this year *',
        type: 'currency',

        name: 'stateEstimatedTaxesAlreadyPaidSoFarThisYear',
      },
    ],
  },
  {
    dependenciesTitle: 'Documents',
    type: 'information',
    subLabel: () => 'Please upload the following',
    dependencies: [
      {
        name: 'latestPaySub',
        type: 'upload',
        label: 'Latest Pay Stub',
      },
      {
        name: 'priorYearTaxReturn',
        type: 'upload',
        label: "Prior Year's Tax Return",
      },
    ],
  },
  {
    dependenciesTitle: 'Submit Tax Information',
    type: 'information',

    dependencies: [
      {
        name: 'notes',
        type: 'textarea',
        label: 'Enter additional information if any',
      },
      {
        name: 'reachPhoneNumber',
        type: 'text',
        label:
          'At what number can your accountant reach you if there are questions?',
      },
      {
        name: 'whatIsTheBestTimeToReachYouAtThisNumber',
        type: 'radio',
        label: 'What is the best time to reach you at this number?',

        options: ['Anytime', 'Morning', 'Afternoon', 'Evening'].map((o) => ({
          label: o,
          value: o,
        })),
      },
      {
        type: 'information',
        label:
          "To the best of my knowledge the information enclosed in this client tax information is correct and includes all income, deductions, and other information necessary for the preparation of this year's income tax returns for which I have adequate records.",

        dependencies: [
          {
            name: 'agreement',

            type: 'checkbox',
            label: 'I agree',
          },
        ],
      },
    ],
  },
] as IBaseField<keyof ITaxEstimateForm>[];

export function FormFields({
  updateValue,
  taxEstimate,
  type,
}: {
  type: 'business' | 'individual';
  updateValue: (key: keyof ITaxEstimateForm, value: any) => void;

  taxEstimate?: Partial<ITaxEstimate>;
}) {
  return (
    <>
      {TaxEstimateFields.map((input, index) => (
        <Fragment key={index}>{renderInput(input)}</Fragment>
      ))}
    </>
  );
}
